import { useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import { Trash2 } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  accountDeletionModule, deletionReady, deletionWhatHappens, DELETE_WORD, patientAccountDeletionModule,
  type AccountDeletionBlocker, type AccountDeletionCodeSent, type AccountDeletionInput, type AccountDeletionMethod,
  type AccountDeletionStatus,
} from '../../lib/api/modules/accountDeletion';
import type { ApiResponse } from '../../types';

export type DeletionAudience = 'staff' | 'patient';

export const deletionStatusKey = (a: DeletionAudience) => ['account-deletion', a] as const;

/** Server refusal code → translated sentence (auth:accountDeletion.errors.*); the server's text otherwise. */
const ERROR_KEYS: Record<string, string> = {
  WRONG_PASSWORD: 'wrongPassword', WRONG_CODE: 'wrongCode', CODE_EXPIRED: 'codeExpired', TYPE_DELETE: 'typeDelete', LOCKED: 'locked',
  RESEND_TOO_SOON: 'resendTooSoon', TOO_MANY_CODES: 'tooManyCodes', ALREADY_SCHEDULED: 'alreadyScheduled',
  ACCOUNT_UPGRADE_REQUIRED: 'upgradeRequired', USE_CODE: 'useCode', NOT_SELF_DELETABLE: 'notAllowed',
  EMAIL_VERIFICATION_EXPIRED: 'codeExpired', NO_ACCOUNT: 'noAccount',
};

export function useDeletionErrorText() {
  const { t } = useTranslation('auth');
  return (r: { error?: string; errorCode?: string }) =>
    r.errorCode && ERROR_KEYS[r.errorCode] ? t(`accountDeletion.errors.${ERROR_KEYS[r.errorCode]}`) : (r.error || t('accountDeletion.errors.generic'));
}

export function blockerKey(b: AccountDeletionBlocker | null | undefined): string {
  switch (b) {
    case 'OWNER_WITH_TEAM': return 'accountDeletion.blocked.ownerWithTeam';
    case 'OWNER_OF_CLINIC': return 'accountDeletion.blocked.ownerOfClinic';
    case 'PLATFORM_ADMIN': return 'accountDeletion.blocked.platformAdmin';
    default: return 'accountDeletion.blocked.clinicMember';
  }
}

export function formatDay(iso: string | null | undefined, lang: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(lang === 'te' ? 'te-IN' : lang === 'hi' ? 'hi-IN' : 'en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

const apiOf = (a: DeletionAudience) => (a === 'staff' ? accountDeletionModule : patientAccountDeletionModule);

/**
 * "Delete my account" in the profile (App Store 5.1.1(v) / Google Play): for an account the person created themselves
 * — a listing doctor, a lab or pharmacy owner, a patient — explains what happens and what is kept, re-confirms with the
 * password or a one-time code, asks for the word DELETE, then signs out everywhere. Clinic-invited staff and owners of
 * clinics with a team are told who can remove them instead.
 */
export function DeleteAccountSection({ audience, signInEmail, onDeleted }: {
  audience: DeletionAudience;
  /** Staff: the email the password is hashed under (as at sign-in). */
  signInEmail?: string;
  /** Called after the deletion is scheduled (the caller signs out). */
  onDeleted: () => void;
}) {
  const { t } = useTranslation('auth');
  const status = useQuery({
    queryKey: deletionStatusKey(audience),
    queryFn: async () => (await apiOf(audience).status()).data ?? null,
    staleTime: 60_000,
  });
  const [open, setOpen] = useState(false);
  const s = status.data;
  if (!s) return null;

  return (
    <Card variant="outlined" sx={{ borderColor: s.canDelete ? 'error.light' : undefined }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700} gutterBottom>{t('accountDeletion.title')}</Typography>
        {!s.canDelete ? (
          <Typography variant="body2" color="text.secondary">{t(blockerKey(s.blocker), { email: s.supportEmail })}</Typography>
        ) : (
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="body2" color="text.secondary">{t('accountDeletion.intro', { count: s.graceDays })}</Typography>
            <Button color="error" variant="outlined" startIcon={<Trash2 size={16} />} onClick={() => setOpen(true)}>
              {t('accountDeletion.open')}
            </Button>
          </Stack>
        )}
      </CardContent>
      {open ? (
        <DeleteAccountDialog audience={audience} status={s} signInEmail={signInEmail} onClose={() => setOpen(false)}
          onDone={(scheduledFor) => {
            setOpen(false);
            toast.success(t('accountDeletion.scheduledToast', { date: formatDay(scheduledFor, document.documentElement.lang || 'en') }));
            onDeleted();
          }} />
      ) : null}
    </Card>
  );
}

function DeleteAccountDialog({ audience, status: s, signInEmail, onClose, onDone }: {
  audience: DeletionAudience; status: AccountDeletionStatus; signInEmail?: string; onClose: () => void; onDone: (scheduledFor: string) => void;
}) {
  const { t, i18n } = useTranslation('auth');
  const errorText = useDeletionErrorText();
  const [method, setMethod] = useState<AccountDeletionMethod>(s.methods.includes('PASSWORD') ? 'PASSWORD' : 'CODE');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState<AccountDeletionCodeSent | null>(null);
  const [code, setCode] = useState('');
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const sendCode = useMutation({
    mutationFn: async () => {
      const r = await apiOf(audience).sendCode();
      if (r.error || !r.data) throw new Error(errorText(r));
      return r.data;
    },
    onSuccess: (d) => { setSent(d); setProblem(null); },
    onError: (e) => setProblem(e instanceof Error ? e.message : t('accountDeletion.errors.generic')),
  });

  const submit = useMutation({
    mutationFn: async () => {
      const input: AccountDeletionInput = method === 'PASSWORD'
        ? { method, password, confirmation: typed.trim(), reason: reason.trim() || undefined }
        : { method, challengeId: sent?.challengeId, code: code.trim(), confirmation: typed.trim(), reason: reason.trim() || undefined };
      const r: ApiResponse<{ scheduledFor: string }> = audience === 'staff'
        ? await accountDeletionModule.schedule(input, signInEmail ?? '')
        : await patientAccountDeletionModule.schedule(input);
      if (r.error || !r.data) throw new Error(errorText(r));
      return r.data;
    },
    onSuccess: (d) => onDone(d.scheduledFor),
    onError: (e) => { setProblem(e instanceof Error ? e.message : t('accountDeletion.errors.generic')); setPassword(''); },
  });

  const ready = deletionReady({ method, password, challengeId: sent?.challengeId ?? null, code, typed });
  const when = new Date(Date.now() + s.graceDays * 86_400_000).toISOString();

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="delete-account-title">
      <DialogTitle id="delete-account-title">{t('accountDeletion.sheetTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>{t('accountDeletion.whatTitle')}</Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0, display: 'grid', gap: 0.75 }}>
              {deletionWhatHappens(s.kind, s.upcomingBookings).map((k) => (
                <Typography component="li" variant="body2" key={k}>
                  {t(`accountDeletion.what.${k}`, { count: s.upcomingBookings, days: s.graceDays, date: formatDay(when, i18n.language) })}
                </Typography>
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>{t('accountDeletion.proveTitle')}</Typography>
            {s.methods.length > 1 ? (
              <ToggleButtonGroup exclusive size="small" value={method} sx={{ mb: 1.5 }}
                onChange={(_, v: AccountDeletionMethod | null) => { if (v) { setMethod(v); setProblem(null); } }}>
                <ToggleButton value="PASSWORD">{t('accountDeletion.byPassword')}</ToggleButton>
                <ToggleButton value="CODE">{t('accountDeletion.byCode')}</ToggleButton>
              </ToggleButtonGroup>
            ) : null}
            {method === 'PASSWORD' ? (
              <TextField fullWidth type="password" autoComplete="current-password" label={t('accountDeletion.password')}
                value={password} onChange={(e) => setPassword(e.target.value)} />
            ) : sent ? (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {t(sent.channel === 'SMS' ? 'accountDeletion.codeSentSms' : 'accountDeletion.codeSentEmail', { hint: sent.hint ?? '' })}
                </Typography>
                <TextField fullWidth label={t('accountDeletion.code')} value={code} autoComplete="one-time-code"
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
                <Box><Button size="small" onClick={() => sendCode.mutate()} disabled={sendCode.isPending}>{t('accountDeletion.resend')}</Button></Box>
              </Stack>
            ) : (
              <Button variant="outlined" onClick={() => sendCode.mutate()} disabled={sendCode.isPending}>
                {t(s.codeChannel === 'SMS' ? 'accountDeletion.sendSms' : 'accountDeletion.sendEmail', { hint: s.codeHint ?? '' })}
              </Button>
            )}
          </Box>

          <TextField fullWidth label={t('accountDeletion.typeLabel', { word: DELETE_WORD })} placeholder={DELETE_WORD} value={typed}
            onChange={(e) => setTyped(e.target.value)} inputProps={{ autoCapitalize: 'characters', autoCorrect: 'off', spellCheck: false }} />
          <TextField fullWidth multiline minRows={2} label={t('accountDeletion.reason')} value={reason}
            inputProps={{ maxLength: 500 }} onChange={(e) => setReason(e.target.value)} />
          {problem ? <Alert severity="error" role="alert">{problem}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('accountDeletion.keep')}</Button>
        <Button color="error" variant="contained" disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
          {t('accountDeletion.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
